import { Fragment, type ReactNode, useEffect, useRef } from 'react';

import {
    answerRunQuestion,
    decideRunToolApproval,
    interruptRun,
    openRunEventStream,
    sendRunMessage,
} from '@apps/web/features/phoebe_chat_v3/api/run_api';
import { recordEventChatClientIssue } from '@apps/web/features/phoebe_chat_v3/api/telemetry';
import {
    UNAVAILABLE_WRITABLE_APPROVAL_SETTINGS,
    type WritableApprovalSettings,
} from '@apps/web/features/phoebe_chat_v3/model/approval_settings_port';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    type AgentProfilePorts,
    type RunSummary,
} from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import {
    orchestratorAgentProfile,
    orchestratorAgentProfileResolver,
} from '@apps/web/features/phoebe_chat_v3/profiles/orchestrator_profile';
import {
    v3AgentProfileResolver,
    v3PhoebeAgentProfile,
    type V3PhoebeSurface,
} from '@apps/web/features/phoebe_chat_v3/profiles/v3_phoebe_profile';
import { ApprovalModeControl } from '@apps/web/features/phoebe_chat_v3/ui/approval_mode_control';
import {
    ChatHarness,
    type ChatHarnessProps,
} from '@apps/web/features/phoebe_chat_v3/ui/chat_harness';
import { RunModelPickerControl } from '@apps/web/features/phoebe_chat_v3/ui/run_model_picker_control';
import { ViewerUserIdContext } from '@apps/web/features/phoebe_chat_v3/ui/viewer_user_context';

const profilePorts: AgentProfilePorts = {
    transport: { openEventStream: openRunEventStream },
    writes: {
        sendMessage: sendRunMessage,
        async answerQuestion(scope, request, signal) {
            const response = await answerRunQuestion(scope, request, signal);
            return { ...response, tool_name: null };
        },
        async decideToolApproval(scope, request, signal) {
            const response = await decideRunToolApproval(
                scope,
                request,
                signal,
            );
            return { ...response, tool_name: null };
        },
        interrupt: interruptRun,
    },
};
const profiles = {
    general: v3PhoebeAgentProfile(profilePorts, 'general'),
    outreach: v3PhoebeAgentProfile(profilePorts, 'outreach'),
    manual_outreach: v3PhoebeAgentProfile(profilePorts, 'manual_outreach'),
    automation_run: v3PhoebeAgentProfile(profilePorts, 'automation_run'),
} satisfies Readonly<Record<V3PhoebeSurface, unknown>>;
const orchestratorProfile = orchestratorAgentProfile(profilePorts);

type HarnessPositionProps = Pick<
    ChatHarnessProps,
    | 'captureRef'
    | 'composerPrefill'
    | 'initialMessage'
    | 'initialDescriptor'
    | 'onPositionChange'
    | 'restorationEntryKey'
    | 'stagedReferences'
    | 'visible'
>;

export type V3RunSurfaceProps = Readonly<{
    readonly approvalSettings?: WritableApprovalSettings;
    readonly differentExperience: ReactNode;
    readonly invalidOrganizationTimeZone: boolean;
    readonly scope: HarnessScope;
    readonly summary: RunSummary;
    readonly outreachId?: string;
    readonly surface?: V3PhoebeSurface;
    /** The viewer's app user id, for sender comparisons in the transcript. */
    readonly viewerUserId?: string | null;
}> &
    HarnessPositionProps;

export function V3RunSurface({
    approvalSettings = UNAVAILABLE_WRITABLE_APPROVAL_SETTINGS,
    captureRef,
    composerPrefill,
    differentExperience,
    initialMessage,
    initialDescriptor,
    invalidOrganizationTimeZone,
    onPositionChange,
    outreachId,
    restorationEntryKey,
    scope,
    stagedReferences,
    summary,
    surface = 'general',
    viewerUserId = null,
    visible = true,
}: V3RunSurfaceProps): ReactNode {
    // The run decides, not the host surface: an orchestrator run opened from
    // the general sidebar is still an orchestrator chat.
    const isOrchestrator = summary.run_type === 'orchestrator';
    const profile = isOrchestrator ? orchestratorProfile : profiles[surface];
    const resolver = isOrchestrator
        ? orchestratorAgentProfileResolver(profile)
        : v3AgentProfileResolver(profile);
    const issueRecorded = useRef(false);
    useEffect(() => {
        if (!invalidOrganizationTimeZone || issueRecorded.current) {
            return;
        }
        issueRecorded.current = true;
        recordEventChatClientIssue({
            profileId: profile.id,
            rendererVersion: profile.rendererVersion,
            reason: 'invalid_organization_timezone',
            lineType: null,
            eventType: null,
            count: 1,
        });
    }, [invalidOrganizationTimeZone, profile.id, profile.rendererVersion]);

    const resolution = resolver.resolve(summary);
    const showApprovalMode =
        summary.agent_runtime === 'v3' && approvalSettings.loaded;

    if (resolution.kind === 'different_experience') {
        return differentExperience;
    }

    return (
        <ViewerUserIdContext.Provider value={viewerUserId}>
            <ChatHarness
                visible={visible}
                captureRef={captureRef}
                composerPrefill={composerPrefill}
                initialDescriptor={initialDescriptor}
                initialMessage={initialMessage}
                onPositionChange={onPositionChange}
                restorationEntryKey={restorationEntryKey}
                scope={scope}
                stagedReferences={stagedReferences}
                profile={resolution.profile}
                referenceSearchOutreachId={outreachId}
                voiceInputFlow={
                    surface === 'general' ? 'general_chat' : 'callout_chat'
                }
                // Keyed off the run, not the hosting surface: an outreach
                // run is shared (any org member can open it) even when
                // viewed through the general-surface run route. The backend
                // derives the flag from the outreach resource link, which
                // covers v3-born runner runs (typed `general`) as well as
                // legacy `outreach_analysis` runs. Owner-scoped chats stay
                // off, so presence never broadcasts without a second
                // possible participant.
                typingPresenceEnabled={
                    summary.is_shared_outreach_run && summary.can_send
                }
                composerDisabledReason={
                    approvalSettings.saving
                        ? 'Wait for the approval mode change to finish.'
                        : null
                }
                composerControls={
                    <Fragment>
                        {showApprovalMode && (
                            <ApprovalModeControl settings={approvalSettings} />
                        )}
                        {!isOrchestrator && (
                            <RunModelPickerControl
                                scope={scope}
                                initialActiveModel={
                                    summary.active_model ?? null
                                }
                                initialResolvedModel={
                                    summary.resolved_model ?? null
                                }
                            />
                        )}
                    </Fragment>
                }
            />
        </ViewerUserIdContext.Provider>
    );
}
